/**************************************************************************************************/
/** @import Node Packages                                                                        **/
/**************************************************************************************************/
import { defineComponent } from 'vue'

/**************************************************************************************************/
/** @import Components/Global                                                                    **/
/**************************************************************************************************/
import * as CompanyLogo from '@/Components/Global/CompanyLogo/template.vue'
import * as DataTable from '@/Components/Global/DataTable/template.vue'
import * as SubmitButton from '@/Components/Global/SubmitButton/template.vue'

/**************************************************************************************************/
/** @import Constants                                                                            **/
/**************************************************************************************************/
import { Component } from '@/Constants/Component'

/**************************************************************************************************/
/** @import Helpers/Utils                                                                        **/
/**************************************************************************************************/
import { PrintHelper } from '@/Helpers/Utils/PrintHelper'

/**************************************************************************************************/
/** @import Mixins                                                                               **/
/**************************************************************************************************/
import MixinBase from '@/Mixins/MixinBase'
import MixinComponent from '@/Mixins/MixinComponent'
import MixinResponsive from '@/Mixins/MixinResponsive'

/**************************************************************************************************/
/** @internal Const @name PopupTable                                                             **/
/**************************************************************************************************/
const PopupTable = defineComponent(
{
	/*************************************************/
	/** @Properties                                 **/
	/*************************************************/
	name: 'PopupTable', mixins: [MixinBase, MixinComponent, MixinResponsive],

	components:
	{
		CompanyLogo: CompanyLogo.default,
		DataTable: DataTable.default,
		SubmitButton: SubmitButton.default
	},

	props:
	{
		numItems: { type: Number, default: Component.VueComponents.Global.DataTable.DefaultValues.ItemsPerPage },
		show: Boolean
	},

	/*************************************************/
	/** @Data                                       **/
	/*************************************************/
	data()
	{
		return {
			header: [] as Array<any>,
			states:
			{
				action: Component.Actions.INSERT,
				currentRow: undefined as any,
				isFetching: false,
				showCustomHeader: false,
				showSelectButton: true,
				subtitle: null as string | null,
				title: 'Place Holder'
			}
		}
	},

	/*************************************************/
	/** @Computed                                   **/
	/*************************************************/
	computed:
	{
		DataTable() : DataTable.DataTableRef
		{
			return this.$refs.dataTable as DataTable.DataTableRef
		}
	},

	/*************************************************/
	/** @Methods                                    **/
	/*************************************************/
	methods:
	{
		GetTitle() : string
		{
			return this.states.title
		},

		IsEmpty() : boolean
		{
			return this.states.currentRow === undefined
		},

		IsVisible() : boolean
		{
			return this.states.showSelectButton === true
		},

		OnPTButtonClick(key: string, row: object, button: Event) : void
		{
			PrintHelper.warning('[PopupTable]:onPTButtonClick event triggered')
			this.$emit('onPTButtonClick', key, row, button)
		},

		OnPTClose() : void
		{
			PrintHelper.warning('[PopupTable]:onPTClose event triggered')
			this.RestoreStates()
			this.$emit('onPTClose', 'PopupTable')
		},

		OnPTCurrentSearchPaginationChanged(page: number, searchParams: any) : void
		{
			PrintHelper.warning('[PopupTable]:onPTCurrentSearchPaginationChanged event triggered')
			this.$emit('onPTCurrentSearchPaginationChanged', page, searchParams)
		},

		OnPTPaginationChanged(page: number) : void
		{
			PrintHelper.warning('[PopupTable]:onPTPaginationChanged event triggered')
			this.$emit('onPTPaginationChanged', page)
		},

		OnPTRefreshButtonClick() : void
		{
			PrintHelper.warning('[PopupTable]:onPTRefreshButtonClick event triggered')
			this.$emit('onPTRefreshButtonClick')
		},

		OnPTSearchButtonClicked(searchParams: any) : void
		{
			PrintHelper.warning('[PopupTable]:onPTSearchButtonClicked event triggered')
			this.$emit('onPTSearchButtonClicked', searchParams)
		},

		OnPTSelect() : void
		{
			PrintHelper.warning('[PopupTable]:onPTSelect event triggered')
			this.$emit('onPTSelect', this.states.currentRow)
			this.RestoreStates()
		},

		OnRowClick(item: object, index: number) : void
		{
			PrintHelper.warning('[PopupTable]:onRowClick():Event triggered')
			this.Set<PopupTableRef['states']>({ currentRow: { item, index } })
			this.$emit('onRowClick', { item, index })
		},

		OnRowDoubleClick(item: object, index: number) : void
		{
			PrintHelper.warning('[PopupTable]:onRowDoubleClick():Event triggered')
			this.RestoreStates()
			this.$emit('onRowDoubleClick', { item, index })
		},

		OnRowsUnselected() : void
		{
			PrintHelper.warning('[PopupTable]:onRowDoubleClick():Event triggered')
			this.Set<PopupTableRef['states']>({ currentRow: undefined })
		},

		RestoreStates() : void
		{
			// Reiniciar los valores propios del componente.
			this.Set<PopupTableRef['states']>({ currentRow: undefined })

			// Aplicar 'Flag' temporal para prevenir comportamiento por Defecto al ejecutar 'clearSortBy'.
			const dataTable = this.DataTable
			dataTable.Set<DataTable.DataTableRef['states']>({ preventDefaultOnInputSearchClear: true })

			// Reiniciar los valores del componente <DataTable>.
			dataTable.ResetFetchState()
			dataTable.ClearSortBy()
			dataTable.ClearData()
		},

		SetCustomHeader(...header: Array<object>) : void
		{
			this.header = header
		},

		SetSelectVisible(bool: boolean) : void
		{
			this.states.showSelectButton = bool
		},

		SetTitle(title: string) : void
		{
			this.states.title = title
		}
	}
})

/**************************************************************************************************/
/** @export Component @name PopupTable                                                           **/
/**************************************************************************************************/
export default PopupTable

/**************************************************************************************************/
/** @export Type @name PopupTableRef                                                             **/
/**************************************************************************************************/
export type PopupTableRef = InstanceType<typeof PopupTable>